import { SDK_VERSION } from './constants';
import { HttpClient } from './http-client';
import { StorageManager } from './storage';
import { ConsentManager } from './consent-manager';
import { getOrCreateShadowHost } from './ui/dom-utils';
import { buildStyles } from './ui/styles';
import { createBanner } from './ui/banner';
import { createModal } from './ui/modal';
import { createFloatingActionButton } from './ui/floating-action-button';
import { createConsentSettingsPanel } from './ui/consent-settings-panel';
import type { VeridionConfig, ConsentMap, SdkConfigResponse, SdkWidgetSettings } from './types';

export type { VeridionConfig, ConsentMap, SdkConfigResponse };

/** Detect privacy policy URL from the integrated page (e.g. <link rel="privacy-policy" href="...">). */
function getPagePolicyUrl(): string | null {
  if (typeof document === 'undefined' || !document.head) return null;
  const link =
    document.querySelector<HTMLLinkElement>('link[rel="privacy-policy"]') ??
    document.querySelector<HTMLLinkElement>('link[rel="privacy"]');
  const href = link?.getAttribute('href')?.trim();
  return href && (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('/')) ? href : null;
}

/** Effective widget settings: page/default policy overrides SDK config, then fallback to config default. */
function getEffectiveSettings(apiSettings: SdkWidgetSettings, options: VeridionConfig): SdkWidgetSettings {
  const pageOrInitUrl = options.privacyPolicyUrl ?? getPagePolicyUrl() ?? apiSettings.privacyPolicyUrl;
  return { ...apiSettings, privacyPolicyUrl: pageOrInitUrl ?? apiSettings.privacyPolicyUrl };
}

/**
 * Main SDK class — the single public API surface.
 *
 * Script tag (recommended: load SDK without async so Veridion is defined before the next script):
 *   <script src="https://your-domain.com/sdk/veridion-sdk.js"></script>
 *   <script>Veridion.init({ apiUrl: '…', publicKey: 'vrd_pub_…' });</script>
 *
 * If you must load the SDK with async/defer, queue init so it runs when the SDK loads:
 *   <script>window._veridionQueue=window._veridionQueue||[];window.Veridion={init:function(o){window._veridionQueue.push(o);}};</script>
 *   <script src="https://your-domain.com/sdk/veridion-sdk.js" async></script>
 *   <script>Veridion.init({ apiUrl: '…', publicKey: 'vrd_pub_…' });</script>
 *
 * Usage (ES module):
 *   import { VeridionSDK } from '@veridion/sdk';
 *   const sdk = VeridionSDK.getInstance();
 *   await sdk.init({ apiUrl: '…', publicKey: 'vrd_pub_…' });
 */
export class VeridionSDK {
  private static instance: VeridionSDK | null = null;

  private consentManager!: ConsentManager;
  private storage!: StorageManager;
  private shadow: ShadowRoot | null = null;
  private bannerEl: HTMLElement | null = null;
  private modalEl: HTMLElement | null = null;
  private fabEl: HTMLElement | null = null;
  private consentPanelEl: HTMLElement | null = null;
  private config: SdkConfigResponse | null = null;
  /** Widget settings with policy URL resolved: page/default first, then SDK config default. */
  private effectiveSettings: SdkWidgetSettings | null = null;
  private initOptions: VeridionConfig | null = null;
  private initialised = false;

  /** SDK semantic version */
  readonly version = SDK_VERSION;

  private constructor() {}

  static getInstance(): VeridionSDK {
    if (!VeridionSDK.instance) {
      VeridionSDK.instance = new VeridionSDK();
    }
    return VeridionSDK.instance;
  }

  /**
   * Initialise the SDK.  Fetches configuration from the API and, if the
   * user hasn't already made consent choices, renders the banner.
   * Idempotent: a second call (e.g. from integrated script tag + queue) is ignored to avoid re-showing the banner.
   */
  async init(options: VeridionConfig): Promise<void> {
    if (this.initialised) return;

    this.initOptions = options;
    const httpClient = new HttpClient({ apiUrl: options.apiUrl, publicKey: options.publicKey });
    const storage = new StorageManager();
    this.storage = storage;

    // Use subjectId from options so config/consents are fetched for the right user (e.g. data-subid or setSubjectId)
    if (options.subjectId?.trim()) {
      storage.setSubjectId(options.subjectId.trim());
    }

    this.consentManager = new ConsentManager(httpClient, storage);
    this.consentManager.onConsentChange = options.onConsentChange;

    this.config = await this.consentManager.initialize();
    this.effectiveSettings = this.config ? getEffectiveSettings(this.config.settings, options) : null;

    // Set up shadow DOM
    const { shadow } = getOrCreateShadowHost();
    this.shadow = shadow;

    // Inject styles
    this.injectStyles();

    // Show banner only when there are pending decisions (no consent for every purpose).
    // If the user already has all decisions (e.g. from API for this subject), do not show the banner.
    if (!this.consentManager.hasAllDecisions()) {
      this.showBanner();
    }

    // When user has already defined consent, show floating action button to manage it
    if (this.consentManager.hasAllDecisions()) {
      this.showFloatingActionButton();
    }

    this.initialised = true;
  }

  /** Programmatically open the preferences modal. */
  open(): void {
    this.ensureInit();
    this.hideBanner();
    this.showModal();
  }

  /** Close all SDK UI (banner + modal + consent panel). Shows FAB if user has defined consent. */
  close(): void {
    this.hideBanner();
    this.hideModal();
    this.hideConsentPanel();
    if (this.initialised && this.consentManager.hasAllDecisions()) {
      this.showFloatingActionButton();
    }
  }

  /** Accept all purposes. */
  async acceptAll(): Promise<void> {
    this.ensureInit();
    await this.consentManager.acceptAll();
    this.close();
  }

  /** Reject all purposes. */
  async rejectAll(): Promise<void> {
    this.ensureInit();
    await this.consentManager.rejectAll();
    this.close();
  }

  /** Get the current consent map { purposeId: status }. */
  getConsents(): ConsentMap {
    this.ensureInit();
    return this.consentManager.getConsents();
  }

  /** Link an authenticated user id to the anonymous subject. */
  async setSubjectId(subjectId: string): Promise<void> {
    this.ensureInit();
    await this.consentManager.setSubjectId(subjectId);
  }

  // ── Private rendering ──

  private injectStyles(): void {
    if (!this.shadow || !this.effectiveSettings) return;
    const existing = this.shadow.querySelector('style[data-veridion]');
    if (existing) existing.remove();

    const style = document.createElement('style');
    style.setAttribute('data-veridion', 'true');
    style.textContent = buildStyles(this.effectiveSettings);
    this.shadow.appendChild(style);
  }

  private showBanner(): void {
    if (!this.shadow || !this.effectiveSettings) return;
    this.hideBanner(); // remove any existing

    this.bannerEl = createBanner(
      this.effectiveSettings,
      {
        onAcceptAll: async () => {
          await this.consentManager.acceptAll();
          this.close();
        },
        onRejectAll: async () => {
          await this.consentManager.rejectAll();
          this.close();
        },
        onManage: () => {
          this.hideBanner();
          this.showModal();
        },
      }
    );

    this.shadow.appendChild(this.bannerEl);
  }

  private hideBanner(): void {
    if (this.bannerEl && this.bannerEl.parentNode) {
      this.bannerEl.parentNode.removeChild(this.bannerEl);
      this.bannerEl = null;
    }
  }

  private showModal(): void {
    if (!this.shadow || !this.config || !this.effectiveSettings) return;
    this.hideModal();

    this.modalEl = createModal(
      this.effectiveSettings,
      this.config.purposes,
      this.consentManager.getConsentsMap(),
      {
        onSave: async (decisions: any) => {
          await this.consentManager.savePreferences(decisions);
          this.close();
        },
        onAcceptAll: async () => {
          await this.consentManager.acceptAll();
          this.close();
        },
        onRejectAll: async () => {
          await this.consentManager.rejectAll();
          this.close();
        },
        onClose: () => this.hideModal(),
      },
      this.config.policyVersions ?? []
    );

    this.shadow.appendChild(this.modalEl);
  }

  private hideModal(): void {
    if (this.modalEl && this.modalEl.parentNode) {
      this.modalEl.parentNode.removeChild(this.modalEl);
      this.modalEl = null;
    }
  }

  private showFloatingActionButton(): void {
    if (!this.shadow || !this.effectiveSettings) return;
    this.hideFloatingActionButton();
    this.fabEl = createFloatingActionButton(this.effectiveSettings, {
      onClick: () => this.openConsentSettingsPanel(),
    });
    this.shadow.appendChild(this.fabEl);
  }

  private hideFloatingActionButton(): void {
    if (this.fabEl && this.fabEl.parentNode) {
      this.fabEl.parentNode.removeChild(this.fabEl);
      this.fabEl = null;
    }
  }

  private async openConsentSettingsPanel(): Promise<void> {
    if (!this.shadow || !this.config || !this.initOptions) return;
    this.hideConsentPanel();
    await this.consentManager.initialize();
    this.config = this.consentManager.getConfig();
    this.effectiveSettings = this.config ? getEffectiveSettings(this.config.settings, this.initOptions!) : null;
    if (!this.config || !this.effectiveSettings) return;
    const subjectId = this.storage.getSubjectId();
    this.consentPanelEl = createConsentSettingsPanel(
      this.effectiveSettings,
      this.config.purposes,
      this.config.consentStates,
      subjectId,
      {
        onWithdraw: async () => {
          await this.consentManager.rejectAll();
          await this.consentManager.initialize();
          this.config = this.consentManager.getConfig();
          this.hideConsentPanel();
        },
        onChangeConsent: () => {
          this.hideConsentPanel();
          this.showModal();
        },
        onClose: () => this.hideConsentPanel(),
      },
    );
    this.shadow!.appendChild(this.consentPanelEl);
  }

  private hideConsentPanel(): void {
    if (this.consentPanelEl && this.consentPanelEl.parentNode) {
      this.consentPanelEl.parentNode.removeChild(this.consentPanelEl);
      this.consentPanelEl = null;
    }
  }

  private ensureInit(): void {
    if (!this.initialised) {
      throw new Error('[Veridion] SDK not initialised. Call Veridion.init() first.');
    }
  }
}

/** Infer apiUrl from the script tag's src (e.g. https://example.com/sdk/veridion-sdk.js -> https://example.com). */
function getApiUrlFromScriptSrc(script: HTMLScriptElement): string {
  const src = script.getAttribute('src') || script.src || '';
  try {
    const url = new URL(src, typeof document !== 'undefined' ? document.baseURI : undefined);
    return url.origin;
  } catch {
    return '';
  }
}

/** If the page has <script id="Veridion31" ... data-blockingmode="auto">, init from data attributes. */
function tryAutoInitFromScriptTag(): void {
  if (typeof document === 'undefined') return;
  const script = document.getElementById('Veridion31') as HTMLScriptElement | null;
  if (!script || script.tagName !== 'SCRIPT') return;
  const blockingMode = (script.getAttribute('data-blockingmode') || '').toLowerCase();
  if (blockingMode !== 'auto') return;
  const publicKey = script.getAttribute('data-public-key')?.trim();
  if (!publicKey) return;
  const apiUrl =
    script.getAttribute('data-api-url')?.trim() || getApiUrlFromScriptSrc(script);
  if (!apiUrl) return;
  const subjectId = script.getAttribute('data-subid')?.trim() || undefined;
  const sdk = VeridionSDK.getInstance();
  sdk.init({ publicKey, apiUrl, subjectId });
}

// ── UMD / global attach ──
const sdk = VeridionSDK.getInstance();

if (typeof window !== 'undefined') {
  (window as any).Veridion = sdk;
  // Run any init options queued before the script loaded (e.g. script with async/defer)
  const q = (window as any)._veridionQueue;
  if (Array.isArray(q)) {
    q.forEach((opts: VeridionConfig) => sdk.init(opts));
    delete (window as any)._veridionQueue;
  }
  // Integrated pattern: <script id="Veridion31" ... data-blockingmode="auto"> auto-inits
  tryAutoInitFromScriptTag();
}

export default sdk;
